import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { InsuranceReportQueries } from '@frontend/api-insurance-eligibility-report';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Modal, ModalControlResponse, SpinningLoader, Text, useAlert } from '@frontend/design-system';
import { useSaveEligibilityReportUpdater } from '../context/SaveEligibilityReportProvider';
import { arrayBufferToBase64, getHTMLBlobData } from '../helpers';
import { insuranceReportTrackingIds } from '../trackingIds';
import { EligibilityReportWritebackErrorContainer } from './EligibilityReportWritebackErrorContainer';
import { WritebackReportContainer } from './WritebackReportContainer';

const SpinningLoaderContainer = () => (
  <div css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
    <SpinningLoader />
  </div>
);

type IEligibilityReportWritebackModalProps = ModalControlResponse & {
  isInsuranceVerificationReportV2Enabled?: boolean;
  PrintableReport: JSX.Element;
  sourceId: string;
  personPMSId: string;
  locationId: string;
  PMSName: string;
  mountTarget?: string;
};
export const EligibilityReportWritebackModal = React.memo(
  ({
    modalProps,
    isInsuranceVerificationReportV2Enabled = true,
    PrintableReport,
    sourceId,
    locationId,
    PMSName,
    personPMSId,
    mountTarget,
  }: IEligibilityReportWritebackModalProps) => {
    const alerts = useAlert();
    const { t } = useTranslation('insuranceEligibilityReport');

    const eligibilityReportRef = useRef<HTMLDivElement>(null);

    const { updateIsEligibilityReportWritebackError, updateIsEligibilityReportWrittenbackInPMS } =
      useSaveEligibilityReportUpdater();

    const [writebackErrorMessage, setWritebackErrorMessage] = useState<string>('');

    const { mutateAsync: initiateEligibilityReportWriteback, isLoading } =
      InsuranceReportQueries.useInitiateEligibilityReportWriteback(locationId);

    const setWritebackError = (errorMessage: string) => {
      if (errorMessage && errorMessage.includes('Sync App')) {
        setWritebackErrorMessage(t("Weave's connection with {{PMSName}} couldn't be established", { PMSName }));
      } else {
        setWritebackErrorMessage(t('Something went wrong during the insurance writeback process'));
      }
    };

    const saveHTMLBlob = async () => {
      if (eligibilityReportRef?.current) {
        const htmlBlobData = getHTMLBlobData(eligibilityReportRef);
        const arradyBufferfromBlobData = await htmlBlobData.arrayBuffer();
        const encodedHTML = arrayBufferToBase64(arradyBufferfromBlobData);
        const pdfWritebackRequest = {
          encodedHtml: encodedHTML,
        };

        initiateEligibilityReportWriteback({
          eligibilityReportWritebackRequest: pdfWritebackRequest,
          sourceId: sourceId,
          personPMSId: personPMSId,
        })
          .then(() => {
            alerts.success(t('Report saved successfully'));
            updateIsEligibilityReportWrittenbackInPMS(true);
            updateIsEligibilityReportWritebackError(false);
            modalProps.onClose();
          })
          .catch((error) => {
            alerts.error(t('Failed to save report'));
            setWritebackError(error?.response?.data?.message ?? '');
            updateIsEligibilityReportWritebackError(true);
          });
      }
    };

    const handleSaveToPMS = () => {
      if (sourceId && personPMSId) {
        saveHTMLBlob();
      } else {
        // fallback alert
        alerts.error('No sourceId or personId found');
      }
    };

    // useEffect will help to unset the error message when the modal is closed
    // so that the error message is not shown when the modal is opened again
    useEffect(() => {
      if (!modalProps.show) {
        setWritebackErrorMessage('');
      }
    }, [modalProps.show]);

    return (
      <>
        <Modal mountTarget={mountTarget} {...modalProps} maxWidth={700}>
          <Modal.Header>{t('Save Insurance Report to PMS')}</Modal.Header>
          <Modal.Body>
            {isLoading && <SpinningLoaderContainer />}{' '}
            {writebackErrorMessage ? (
              <EligibilityReportWritebackErrorContainer
                errorMessage={writebackErrorMessage}
                setWritebackErrorMessage={setWritebackErrorMessage}
                PMSName={PMSName ?? ''}
                closeModal={modalProps.onClose}
                PrintableReport={PrintableReport}
              />
            ) : (
              !isLoading && (
                <Text css={{ margin: theme.spacing(3, 0) }}>{t('The report will be saved in the PMS')}</Text>
              )
            )}
          </Modal.Body>
          {!writebackErrorMessage && (
            <Modal.Actions
              primaryLabel={t('Save to PMS')}
              onPrimaryClick={handleSaveToPMS}
              secondaryLabel={t('Cancel')}
              onSecondaryClick={modalProps.onClose}
              disablePrimary={isLoading}
              primaryTrackingId={insuranceReportTrackingIds.writebackModalSaveToPMSBtn}
              secondaryTrackingId={insuranceReportTrackingIds.writebackModalSaveToPMSBtn}
            />
          )}
        </Modal>
        {modalProps.show && (
          <WritebackReportContainer
            isInsuranceVerificationReportV2Enabled={isInsuranceVerificationReportV2Enabled}
            css={writebackReportContainerStyle(modalProps.show)}
            ref={eligibilityReportRef}
            PrintableReport={PrintableReport}
            mountTarget={mountTarget}
          />
        )}
      </>
    );
  }
);

EligibilityReportWritebackModal.displayName = 'EligibilityReportWritebackModal';

// using visibility instead of display to avoid re-rendering of the component / DOM
const writebackReportContainerStyle = (renderWritebackReport: boolean) =>
  css({
    visibility: renderWritebackReport ? 'visible' : 'hidden',
    overflow: renderWritebackReport ? 'scroll' : 'hidden',
  });
